"use client"

import { toast } from "sonner"
import { useModalStore } from "@/hooks/useModal"
import { Dialog, DialogContent } from "@/components/molecules/Dialog/content"
import useLocaleStore from "@/app/stores/useLocaleStore"
import { AlertTriangle, CheckCircle } from "lucide-react" // Import Lucide icons

const actionConfig = {
    delete: {
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 text-red-500"
                viewBox="0 0 20 20"
                fill="currentColor"
            >
                <path
                    fillRule="evenodd"
                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                />
            </svg>
        ),
        color: "red",
        buttonClass: "border-red-500 bg-red-500 hover:bg-red-600",
    },
    publish: {
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 text-green-500"
                viewBox="0 0 20 20"
                fill="currentColor"
            >
                <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                />
            </svg>
        ),
        color: "green",
        buttonClass: "border-green-500 bg-green-500 hover:bg-green-600",
    },
    unpublish: {
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 text-yellow-500"
                viewBox="0 0 20 20"
                fill="currentColor"
            >
                <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                />
            </svg>
        ),
        color: "yellow",
        buttonClass: "border-yellow-500 bg-yellow-500 hover:bg-yellow-600",
    },
    warning: {
        icon: <AlertTriangle className="h-16 w-16 text-yellow-500" />,
        color: "yellow",
        buttonClass: "border-yellow-500 bg-yellow-500 hover:bg-yellow-600",
    },
    activate: {
        icon: <CheckCircle className="h-16 w-16 text-green-500" />,
        color: "green",
        buttonClass: "border-green-500 bg-green-500 hover:bg-green-600",
    },
}

const actionLabels = {
    delete: { en: "Delete", am: "ሰርዝ" },
    publish: { en: "Publish", am: "ይለጠፍ" },
    unpublish: { en: "Unpublish", am: "አይለጠፍ" },
    warning: { en: "Confirm", am: "አረጋግጥ" },
    activate: { en: "Activate", am: "አግብር" },
}

export const AlertModal = () => {
    const { modals, closeModal, alertData } = useModalStore()
    const currentLang = useLocaleStore().locale

    if (!alertData) return null

    const config = actionConfig[alertData.action]
    const label = actionLabels[alertData.action][currentLang === "am" ? "am" : "en"]

    const handleConfirm = async () => {
        try {
            await alertData.onConfirm()
            closeModal("alert")
        } catch (err) {
            toast.error(`Failed to ${alertData.action}.`)
            console.error(`Error during ${alertData.action}:`, err)
            closeModal("alert")
        }
    }

    return (
        <Dialog open={!!modals["alert"]} onOpenChange={() => closeModal("alert")}>
            <DialogContent className="bg-white p-4 dark:bg-gray-900 sm:max-w-[625px]">
                <div className="flex flex-col items-center p-5 text-center">
                    {config.icon}

                    <h3 className="mt-4 text-xl font-bold">{alertData.title}</h3>
                    <p className="mt-2 text-sm text-gray-500">{alertData.description}</p>
                    {alertData.itemTitle && (
                        <p className="mt-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
                            "{alertData.itemTitle}"
                        </p>
                    )}
                </div>

                <div className="mt-4 flex justify-center gap-4">
                    <button
                        className="rounded-full border bg-white px-5 py-2 text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-100 hover:shadow-md"
                        onClick={() => closeModal("alert")}
                    >
                        {currentLang === "am" ? "ተመለስ" : "Cancel"}
                    </button>
                    <button
                        className={`rounded-full border px-5 py-2 text-sm font-medium text-white shadow-sm hover:shadow-md ${config.buttonClass}`}
                        onClick={handleConfirm}
                    >
                        {label}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    )
}